import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AufgabeAdmin, category, FallaufgabeAdmin, TeilaufgabeAdmin } from "@reflact/ipkv-types";
import { firstValueFrom } from 'rxjs';
import { AufgabenService } from './aufgaben-service';
import { TeilaufgabenService } from './teilaufgaben-service';

type FallaufgabeAdminWithDocuments = FallaufgabeAdmin & {
  documentIds?: string[];
};

@Injectable({
  providedIn: 'root'
})
export class FallaufgabenService {
  private http = inject(HttpClient);
  private aufgabenService = inject(AufgabenService);
  private teilaufgabenService = inject(TeilaufgabenService);
  private baseRoute = "/api/admin/fallaufgabe";
  public fallaufgaben: FallaufgabeAdminWithDocuments[] = [];
  public selectedFallaufgabe: FallaufgabeAdminWithDocuments = { _id: '', createdAt: '', type: 'exam', name: '', state: 'draft', updatedAt: '', category: 'Abrechnungsmanager/-in Arztpraxis', workingDurationMinutes: 0, bewertung: { bestandenAb: 4.25, schluessel: [] }, version: 0, versionsUnique: '', hints: '', documentIds: [] };

  public async changeState(updateOpts: { _id: string, state: 'live' | 'archived' }) {
    const updateResult = await firstValueFrom(this.http.post<FallaufgabeAdmin>(this.baseRoute + "/state", updateOpts));
    const index = this.fallaufgaben.findIndex((fa) => fa._id === updateResult._id);
    if (index >= 0 && this.fallaufgaben[index]) this.fallaufgaben[index] = updateResult;
    if (updateResult._id === this.selectedFallaufgabe._id) this.selectedFallaufgabe = updateResult;
  }

  public async create(name: string, category: category, type: FallaufgabeAdmin['type']) {
    const createResult = await firstValueFrom(this.http.post<FallaufgabeAdminWithDocuments>(this.baseRoute + "/create", { name, category, type }));
    createResult.documentIds = createResult.documentIds ?? [];
    this.fallaufgaben.push(createResult);
    return createResult._id;
  }

  public async createDuplicate(_id: string) {
    const createResult = await firstValueFrom(this.http.post<{ draft: FallaufgabeAdminWithDocuments }>(this.baseRoute + "/createversion", { _id }));
    createResult.draft.documentIds = createResult.draft.documentIds ?? [];
    this.fallaufgaben.push(createResult.draft);
  }

  public async copy(copyOpts: { _id: string, name: string }) {
    const copyResult = await firstValueFrom(this.http.post<{ copy: FallaufgabeAdminWithDocuments }>(this.baseRoute + "/copy", copyOpts));
    copyResult.copy.documentIds = copyResult.copy.documentIds ?? [];
    this.fallaufgaben.push(copyResult.copy);
    return copyResult.copy._id;
  }

  public async update() {
    const updateResult = await firstValueFrom(this.http.post<FallaufgabeAdminWithDocuments>(this.baseRoute + "/update", this.selectedFallaufgabe));
    updateResult.documentIds = updateResult.documentIds ?? [];
    const index = this.fallaufgaben.findIndex((fa) => fa._id === updateResult._id);
    if (index >= 0 && this.fallaufgaben[index]) this.fallaufgaben[index] = updateResult;
    if (updateResult._id === this.selectedFallaufgabe._id) this.selectedFallaufgabe = updateResult;
  }

  public async delete(_id: string) {
    await firstValueFrom(this.http.post<FallaufgabeAdmin>(this.baseRoute + "/delete", { _id }));
    await this.list();
  }

  public async list() {
    const data = await firstValueFrom(this.http.get<FallaufgabeAdminWithDocuments[]>(this.baseRoute + "/list"));
    this.fallaufgaben = (data || []).map((item) => ({ ...item, documentIds: item.documentIds ?? [] })).sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }

  public set(id: string) {
    const fallaufgabe = this.fallaufgaben.find(fa => fa._id === id);
    if (fallaufgabe) this.selectedFallaufgabe = fallaufgabe;
  }

  public reset() {
    this.selectedFallaufgabe = { _id: '', type: 'exam', createdAt: '', name: '', state: 'draft', updatedAt: '', category: 'Abrechnungsmanager/-in Arztpraxis', workingDurationMinutes: 0, bewertung: { bestandenAb: 4.25, schluessel: [] }, version: 0, versionsUnique: '', hints: '', documentIds: [] };
  }

  public async get(id: string) {
    const data = await firstValueFrom(this.http.get<{ aufgaben: AufgabeAdmin[], teilaufgaben: TeilaufgabeAdmin[] }>(this.baseRoute + "/get/" + id));
    this.aufgabenService.sort(data.aufgaben);
    this.teilaufgabenService.sort(data.teilaufgaben);
  }

  public getFallAufgabeById(fallaufgabenId: string) {
    return this.fallaufgaben.find(fa => fa._id == fallaufgabenId)
  }
}
